// OrdenarCompra.js
// Panel del comprador para decidir en qué orden quiere ver los productos al
// hacer una compra: agrupados por categoría > subcategoría (con acordeón,
// para no tener que renderizar los ~1400 productos a la vez — con drag&drop
// solo se montan los productos de la subcategoría que esté abierta en cada
// momento).
//
// El orden se guarda en el backend (tabla orden_compra) y también, al vuelo,
// en localStorage: si el usuario cierra sin pulsar "Guardar", al volver a
// entrar se recupera ese borrador (con el aviso en rojo de que aún no está
// guardado) en vez de perder el trabajo.
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import AuthImage from "../../components/AuthImage";

const ORDEN_VACIO = { destacados: [], categorias: [], subcategorias: {}, productos: {} };

// Mismo sprite de iconos de categoría que usa CrearCompras.js (supervisora),
// para que se reconozcan de un vistazo también aquí.
const nrmKey = (s) => String(s || "")
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "")
  .toLowerCase()
  .trim();

const ICON_POSITIONS = {
  [nrmKey("Aceite, especias y salsas")]: "-84px -28px",
  [nrmKey("Agua y refrescos")]: "-308px -28px",
  [nrmKey("Aperitivos")]: "-252px -28px",
  [nrmKey("Arroz, legumbres y pasta")]: "-224px -28px",
  [nrmKey("Azúcar, caramelos y chocolate")]: "-112px 0",
  [nrmKey("Bodega")]: "0 0",
  [nrmKey("Cacao, café e infusiones")]: "-168px 0",
  [nrmKey("Carne")]: "-28px -28px",
  [nrmKey("Cereales y galletas")]: "-140px 0",
  [nrmKey("Charcutería y quesos")]: "-84px 0",
  [nrmKey("Congelados")]: "-224px 0",
  [nrmKey("Conservas, caldos y cremas")]: "-196px -28px",
  [nrmKey("Cuidado facial y corporal")]: "-280px -28px",
  [nrmKey("Fruta y verdura")]: "-252px 0",
  [nrmKey("Huevos, leche y mantequilla")]: "-56px -28px",
  [nrmKey("Limpieza y hogar")]: "-308px 0",
  [nrmKey("Marisco y pescado")]: "-196px 0",
  [nrmKey("Panadería y pastelería")]: "-56px 0",
  [nrmKey("Pizzas y platos preparados")]: "-168px -28px",
  [nrmKey("Postres y yogures")]: "-336px -28px",
  [nrmKey("Zumos")]: "-336px 0",
};

const IconoCategoria = ({ nombre }) => {
  const pos = ICON_POSITIONS[nrmKey(nombre)];
  if (!pos) return null;
  return (
    <span aria-hidden style={{
      display: "inline-block", width: 28, height: 28, flexShrink: 0,
      backgroundImage: "url('/images/category-icons.svg')",
      backgroundRepeat: "no-repeat", backgroundSize: "auto", backgroundPosition: pos,
    }} />
  );
};

function reordenar(arr, from, to) {
  const copia = [...arr];
  const [item] = copia.splice(from, 1);
  copia.splice(to, 0, item);
  return copia;
}

// Combina el orden guardado (backend o borrador) con el catálogo actual:
// descarta ids que ya no existen y añade al final, en el orden "de fábrica"
// que ya trae el catálogo, cualquier categoría/subcategoría/producto nuevo.
//
// Un producto puede estar guardado en la lista de una subcategoría que NO es
// la suya de catálogo (el comprador lo movió a propósito, para mezclar
// productos de varias subcategorías/categorías que en el súper están juntos
// físicamente). Por eso "faltantes" se calcula en dos pasadas: primero se ve
// qué productos están YA colocados en CUALQUIER lista guardada, y solo se
// añaden a su subcategoría de catálogo los que no estén colocados en ningún
// sitio — si no, aparecerían duplicados (en su sitio de catálogo Y donde los
// movió el comprador).
function mezclarOrden(orden, productosById, categorias, subPorCat, prodPorSub) {
  const catDisponibles = categorias.map((c) => c.id_categoria);
  const catGuardado = (orden.categorias || []).filter((id) => catDisponibles.includes(id));
  const catFaltantes = catDisponibles.filter((id) => !catGuardado.includes(id));
  const catOrder = [...catGuardado, ...catFaltantes];

  const subcategorias = {};
  catOrder.forEach((idCat) => {
    const disponibles = (subPorCat[idCat] || []).map((s) => s.id_subcategoria);
    const guardado = ((orden.subcategorias || {})[idCat] || []).filter((id) => disponibles.includes(id));
    const faltantes = disponibles.filter((id) => !guardado.includes(id));
    subcategorias[idCat] = [...guardado, ...faltantes];
  });

  const colocados = new Set();
  Object.values(subcategorias).forEach((lista) => {
    lista.forEach((idSub) => {
      ((orden.productos || {})[idSub] || []).forEach((id) => {
        if (productosById.has(id)) colocados.add(id);
      });
    });
  });

  const productos = {};
  Object.values(subcategorias).forEach((lista) => {
    lista.forEach((idSub) => {
      const guardado = ((orden.productos || {})[idSub] || []).filter((id) => productosById.has(id));
      const faltantes = (prodPorSub[idSub] || []).filter((id) => !colocados.has(id));
      productos[idSub] = [...guardado, ...faltantes];
    });
  });

  return { destacados: [], categorias: catOrder, subcategorias, productos };
}

const AUTOSCROLLER_OPTIONS = {
  startFromPercentage: 0.15,
  maxScrollAtPercentage: 0.22,
  maxPixelScroll: 18,
  ease: (t) => t,
  durationDampening: { stopDampeningAt: 2000, accelerateAt: 800 },
};

const st = {
  handle: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 46, alignSelf: "stretch", flexShrink: 0,
    color: "#b6bdc9", fontSize: 26, userSelect: "none", touchAction: "none",
  },
};

function imagenSrc(p) {
  if (typeof p?.imagen !== "string" || !p.imagen) return null;
  return p.imagen.startsWith("http") ? p.imagen : `/api/productos_sustitutivos/${p.imagen}`;
}

const FotoProducto = ({ p, onClick }) => {
  const [error, setError] = useState(false);
  const src = imagenSrc(p);
  if (!src || error) {
    return (
      <div style={{
        width: 34, height: 34, borderRadius: 7, background: "#f1f5f9", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
      }}>🛒</div>
    );
  }
  return (
    <AuthImage
      src={src} alt="" loading="lazy" onError={() => setError(true)}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      style={{
        width: 34, height: 34, borderRadius: 7, objectFit: "contain", background: "#f8fafc", flexShrink: 0,
        cursor: onClick ? "zoom-in" : "default",
      }}
    />
  );
};

const ProductoRow = ({ p, index, onVerFoto, origen }) => (
  <Draggable draggableId={`p-${p.id}`} index={index}>
    {(provided, snapshot) => (
      <div ref={provided.innerRef} {...provided.draggableProps} style={{
        display: "flex", alignItems: "center", gap: 9, padding: "7px 0 7px 9px",
        background: snapshot.isDragging ? "#eef2ff" : origen ? "#f5f3ff" : "#fff",
        borderRadius: 9, border: origen ? "1px solid #ddd6fe" : "1px solid #eef0f4", marginBottom: 6,
        boxShadow: snapshot.isDragging ? "0 10px 24px rgba(79,70,229,0.3)" : "none",
        ...provided.draggableProps.style,
      }}>
        <FotoProducto p={p} onClick={() => onVerFoto(p)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {p.nombre}
          </div>
          {origen ? (
            <div style={{ fontSize: 10.5, color: "#7c3aed", fontWeight: 700 }}>movido de: {origen}</div>
          ) : p.cantidad != null && p.cantidad !== "" && (
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{p.cantidad}</div>
          )}
        </div>
        <div {...provided.dragHandleProps} style={{ ...st.handle, cursor: snapshot.isDragging ? "grabbing" : "grab" }} title="Arrastra para reordenar">⠿</div>
      </div>
    )}
  </Draggable>
);

export default function OrdenarCompra() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.user || user?.usuario || "anon";

  // El orden es distinto por supermercado (la disposición de pasillos no es
  // la misma en uno que en otro), pero solo tiene sentido preguntar a quien
  // de verdad compra en más de uno (pisos de empresa CH/CM). Un comprador
  // con pisos solo de CH-SS no tiene un supermercado real que elegir: se le
  // fija "M" sin preguntar nada y listo, un único orden.
  // null = todavía no se sabe si hace falta elegir; una vez se sabe, si hace
  // falta, "supermercado" se queda en null hasta que el comprador elige en
  // el paso bloqueante de abajo (justo al entrar, tras pulsar "Ordenar compra").
  const [necesitaEleccion, setNecesitaEleccion] = useState(null);
  const [supermercado, setSupermercado] = useState(null);
  const draftKey = supermercado ? `orden_compra_draft_${username}_${supermercado}` : null;

  useEffect(() => {
    let cancelado = false;
    axios.get("/api/orden-compra/necesita-eleccion").then((res) => {
      if (cancelado) return;
      const necesita = !!res.data?.necesita_eleccion;
      setNecesitaEleccion(necesita);
      if (!necesita) setSupermercado("M");
    }).catch(() => {
      if (cancelado) return;
      // Si falla la comprobación, mejor preguntar (opción más segura) que
      // asumir un supermercado que podría no ser el correcto.
      setNecesitaEleccion(true);
    });
    return () => { cancelado = true; };
  }, []);

  function elegirSupermercadoInicial(s) {
    setSupermercado(s);
  }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fotoAmpliada, setFotoAmpliada] = useState(null);

  const [productosById, setProductosById] = useState(new Map());
  const [categorias, setCategorias] = useState([]); // [{id_categoria, categoria}]
  const [subPorCat, setSubPorCat] = useState({}); // id_categoria -> [{id_subcategoria, subcategoria}]
  const [prodPorSub, setProdPorSub] = useState({}); // id_subcategoria -> [id, ...] (universo completo)

  const [orden, setOrden] = useState(ORDEN_VACIO);
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [expandedSubs, setExpandedSubs] = useState(new Set());
  const [catalogoListo, setCatalogoListo] = useState(false);

  // El catálogo (productos/categorías/subcategorías) es el mismo para
  // cualquier supermercado: se carga una sola vez.
  useEffect(() => { cargarCatalogo(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function cargarCatalogo() {
    try {
      const resCat = await axios.get("/api/productos/");
      const pMap = new Map();
      const cats = [];
      const subsByCat = {};
      const prodsBySub = {};
      (resCat.data || []).forEach((catBlock) => {
        catBlock.subcategorias.forEach((subBlock) => {
          subBlock.productos.forEach((p) => {
            pMap.set(p.id, p);
            const idCat = p.id_categoria;
            const idSub = p.id_subcategoria;
            if (!prodsBySub[idSub]) prodsBySub[idSub] = [];
            prodsBySub[idSub].push(p.id);
            if (!subsByCat[idCat]) subsByCat[idCat] = [];
            if (!subsByCat[idCat].some((s) => s.id_subcategoria === idSub)) {
              subsByCat[idCat].push({ id_subcategoria: idSub, subcategoria: subBlock.subcategoria });
            }
            if (!cats.some((c) => c.id_categoria === idCat)) {
              cats.push({ id_categoria: idCat, categoria: catBlock.categoria });
            }
          });
        });
      });
      setProductosById(pMap);
      setCategorias(cats);
      setSubPorCat(subsByCat);
      setProdPorSub(prodsBySub);
      setCatalogoListo(true);
    } catch (e) {
      setError("❌ No se pudieron cargar los productos. Inténtalo de nuevo.");
      setLoading(false);
    }
  }

  // El orden sí es distinto por supermercado: se recarga en cuanto se sabe
  // cuál toca (fijo, o recién elegido) y el catálogo ya está listo.
  useEffect(() => {
    if (!catalogoListo || !supermercado) return;
    cargarOrden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogoListo, supermercado]);

  async function cargarOrden() {
    setLoading(true); setError("");
    try {
      const resOrden = await axios.get(`/api/orden-compra/mio?supermercado=${supermercado}`);
      const ordenBackend = (resOrden.data && resOrden.data.orden) || ORDEN_VACIO;
      let ordenBase = ordenBackend;
      let esBorrador = false;
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const draft = JSON.parse(raw);
          if (JSON.stringify(draft) !== JSON.stringify(ordenBackend)) {
            ordenBase = draft;
            esBorrador = true;
          }
        }
      } catch (e) { /* localStorage vacío o corrupto: se ignora */ }

      setOrden(mezclarOrden(ordenBase, productosById, categorias, subPorCat, prodPorSub));
      setDirty(esBorrador);
    } catch (e) {
      setError("❌ No se pudo cargar tu orden guardado. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  // Aplica un cambio de orden hecho por el usuario (arrastrar, restablecer):
  // guarda el borrador en localStorage y marca "sin guardar" explícitamente
  // en el mismo sitio donde ocurre el cambio real. Así el "orden" que se
  // carga desde el backend (cargar(), más abajo) nunca pasa por aquí y nunca
  // se marca como sin guardar solo por haberse mostrado.
  function persistir(next) {
    setOrden(next);
    try { localStorage.setItem(draftKey, JSON.stringify(next)); } catch (e) { /* localStorage no disponible */ }
    setDirty(true);
  }

  function toggleCat(id) {
    setExpandedCats((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  }
  function toggleSub(id) {
    setExpandedSubs((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  }

  function onDragEnd(result) {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === "categorias") {
      persistir({ ...orden, categorias: reordenar(orden.categorias, source.index, destination.index) });
    } else if (type.startsWith("sub:")) {
      const idCat = type.slice(4);
      persistir({ ...orden, subcategorias: { ...orden.subcategorias, [idCat]: reordenar(orden.subcategorias[idCat] || [], source.index, destination.index) } });
    } else if (type === "producto") {
      // Los productos pueden arrastrarse entre subcategorías (incluso de
      // categorías distintas) para mezclar los que en el súper están juntos
      // físicamente, aunque no sean de la misma subcategoría de catálogo.
      const idSubOrigen = source.droppableId.slice(5); // "prod:<id>"
      const idSubDestino = destination.droppableId.slice(5);
      if (idSubOrigen === idSubDestino) {
        persistir({ ...orden, productos: { ...orden.productos, [idSubOrigen]: reordenar(orden.productos[idSubOrigen] || [], source.index, destination.index) } });
      } else {
        const origenLista = [...(orden.productos[idSubOrigen] || [])];
        const [idProd] = origenLista.splice(source.index, 1);
        const destinoLista = [...(orden.productos[idSubDestino] || [])];
        destinoLista.splice(destination.index, 0, idProd);
        persistir({ ...orden, productos: { ...orden.productos, [idSubOrigen]: origenLista, [idSubDestino]: destinoLista } });
      }
    }
  }

  async function guardar() {
    setGuardando(true); setError("");
    try {
      await axios.put("/api/orden-compra/mio", { orden, supermercado });
      try { localStorage.removeItem(draftKey); } catch (e) { /* ignore */ }
      setDirty(false);
      setGuardadoOk(true);
      setTimeout(() => setGuardadoOk(false), 2500);
    } catch (e) {
      setError("❌ No se pudo guardar. Comprueba tu conexión e inténtalo de nuevo.");
    } finally {
      setGuardando(false);
    }
  }

  function restablecer() {
    if (!window.confirm("¿Restablecer el orden por defecto? Se perderá el orden manual que hayas hecho.")) return;
    persistir(mezclarOrden(ORDEN_VACIO, productosById, categorias, subPorCat, prodPorSub));
  }

  if (necesitaEleccion === null) {
    return <div style={{ minHeight: "100vh", background: "#f0f4f8", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }}>⏳ Cargando…</div>;
  }

  // Paso bloqueante justo al entrar (tras pulsar "Ordenar compra"): solo se
  // muestra a quien de verdad compra en más de un supermercado. El resto
  // (p.ej. compradores con pisos solo de empresa CH-SS) pasa directo, sin
  // preguntar nada, porque ya se fijó "M" más arriba.
  if (necesitaEleccion && !supermercado) {
    return (
      <div style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: "30px 26px", maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>🛒</div>
          <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 8, color: "#1e293b" }}>¿Qué orden quieres editar?</div>
          <div style={{ fontSize: 13.5, color: "#64748b", marginBottom: 20 }}>El orden de los productos es distinto en cada supermercado. Elige cuál quieres organizar ahora.</div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            {["M", "B"].map((s) => (
              <button key={s} onClick={() => elegirSupermercadoInicial(s)} style={{
                flex: 1, padding: "16px 0", borderRadius: 12, border: "2px solid #e5e7eb", background: "#f8fafc",
                fontSize: 20, fontWeight: 800, color: "#1e293b", cursor: "pointer",
              }}>
                {s}
              </button>
            ))}
          </div>
          <button onClick={() => navigate(-1)} style={{ marginTop: 18, background: "none", border: "none", color: "#94a3b8", fontSize: 13, cursor: "pointer" }}>
            ← Volver
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div style={{ minHeight: "100vh", background: "#f0f4f8", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }}>⏳ Cargando catálogo…</div>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8", paddingBottom: 90 }}>
      <div style={{ background: "linear-gradient(135deg,#312e81,#1e1b4b)", color: "#fff", padding: "14px 16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "8px 14px" }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Preferencia personal{necesitaEleccion ? ` · ${supermercado}` : ""}</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>🔀 Ordenar compra</div>
        </div>
      </div>

      {error && <div style={{ margin: "12px 14px", padding: "10px 14px", borderRadius: 10, background: "#fee2e2", color: "#991b1b", fontSize: 13, fontWeight: 600 }}>{error}</div>}

      <DragDropContext onDragEnd={onDragEnd} autoScrollerOptions={AUTOSCROLLER_OPTIONS}>
          <div style={{ padding: "0 14px 14px" }}>
            <Droppable droppableId="categorias" type="categorias">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {orden.categorias.map((idCat, idxCat) => {
                    const meta = categorias.find((c) => c.id_categoria === idCat);
                    if (!meta) return null;
                    const abierta = expandedCats.has(idCat);
                    const nSubs = (orden.subcategorias[idCat] || []).length;
                    return (
                      <Draggable key={idCat} draggableId={`cat-${idCat}`} index={idxCat}>
                        {(providedCat, snapshotCat) => (
                          <div ref={providedCat.innerRef} {...providedCat.draggableProps} style={{
                            background: "#e0e7ff", borderRadius: 14, border: "1px solid #a5b4fc", marginBottom: 10,
                            boxShadow: snapshotCat.isDragging ? "0 10px 26px rgba(49,46,129,0.3)" : "none",
                            ...providedCat.draggableProps.style,
                          }}>
                            <div style={{ display: "flex", alignItems: "stretch", gap: 4 }}>
                              <div onClick={() => toggleCat(idCat)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0 10px 14px", cursor: "pointer", flex: 1, minWidth: 0 }}>
                                <IconoCategoria nombre={meta.categoria} />
                                <span style={{ fontSize: 15, fontWeight: 800, color: "#312e81", flex: 1 }}>{meta.categoria}</span>
                                <span style={{ fontSize: 11.5, color: "#4338ca", fontWeight: 700 }}>{nSubs} subcat.</span>
                                <span style={{ fontSize: 13, color: "#4338ca", transform: abierta ? "rotate(180deg)" : "none" }}>▾</span>
                              </div>
                              <div {...providedCat.dragHandleProps} style={{ ...st.handle, height: 56, color: "#818cf8", cursor: snapshotCat.isDragging ? "grabbing" : "grab" }} title="Arrastra para reordenar la categoría">⠿</div>
                            </div>
                            {abierta && (
                              <div style={{ padding: "0 14px 12px" }}>
                                <Droppable droppableId={`sub:${idCat}`} type={`sub:${idCat}`}>
                                  {(providedSub) => (
                                    <div ref={providedSub.innerRef} {...providedSub.droppableProps}>
                                      {(orden.subcategorias[idCat] || []).map((idSub, idxSub) => {
                                        const metaSub = (subPorCat[idCat] || []).find((s) => s.id_subcategoria === idSub);
                                        if (!metaSub) return null;
                                        const subAbierta = expandedSubs.has(idSub);
                                        const nProd = (orden.productos[idSub] || []).length;
                                        return (
                                          <Draggable key={idSub} draggableId={`sub-${idSub}`} index={idxSub}>
                                            {(providedS, snapshotS) => (
                                              <div ref={providedS.innerRef} {...providedS.draggableProps} style={{
                                                background: "#eef2ff", borderRadius: 10, border: "1px solid #c7d2fe", marginBottom: 8,
                                                boxShadow: snapshotS.isDragging ? "0 8px 20px rgba(49,46,129,0.25)" : "none",
                                                ...providedS.draggableProps.style,
                                              }}>
                                                <div style={{ display: "flex", alignItems: "stretch", gap: 2 }}>
                                                  <div onClick={() => toggleSub(idSub)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0 9px 10px", cursor: "pointer", flex: 1, minWidth: 0 }}>
                                                    <span style={{ fontSize: 13.5, fontWeight: 700, color: "#3730a3", flex: 1 }}>{metaSub.subcategoria}</span>
                                                    <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 700 }}>{nProd}</span>
                                                    <span style={{ fontSize: 12, color: "#6366f1", transform: subAbierta ? "rotate(180deg)" : "none" }}>▾</span>
                                                  </div>
                                                  <div {...providedS.dragHandleProps} style={{ ...st.handle, width: 40, height: 50, fontSize: 22, color: "#a5b4fc", cursor: snapshotS.isDragging ? "grabbing" : "grab" }} title="Arrastra para reordenar la subcategoría">⠿</div>
                                                </div>
                                                {subAbierta && (
                                                  <div style={{ padding: "0 10px 10px" }}>
                                                    <Droppable droppableId={`prod:${idSub}`} type="producto">
                                                      {(providedP) => (
                                                        <div ref={providedP.innerRef} {...providedP.droppableProps}>
                                                          {(orden.productos[idSub] || []).map((idProd, idxProd) => {
                                                            const p = productosById.get(idProd);
                                                            if (!p) return null;
                                                            const esAjeno = p.id_subcategoria !== idSub;
                                                            return (
                                                              <ProductoRow key={idProd} p={p} index={idxProd} onVerFoto={setFotoAmpliada}
                                                                origen={esAjeno ? p.subcategoria : null} />
                                                            );
                                                          })}
                                                          {providedP.placeholder}
                                                        </div>
                                                      )}
                                                    </Droppable>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </Draggable>
                                        );
                                      })}
                                      {providedSub.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            <button onClick={restablecer} style={{ marginTop: 10, background: "none", border: "1.5px solid #cbd5e1", color: "#64748b", borderRadius: 10, padding: "9px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              ↺ Restablecer orden por defecto
            </button>
          </div>
      </DragDropContext>

      <div style={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 50,
        padding: "12px 16px", paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        background: dirty ? "#dc2626" : "#fff",
        borderTop: dirty ? "none" : "1px solid #e2e8f0",
        boxShadow: "0 -4px 16px rgba(0,0,0,0.12)",
      }}>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: dirty ? "#fff" : "#16a34a", minWidth: 180 }}>
          {dirty ? "⚠️ Tienes cambios sin guardar — pulsa Guardar para que se apliquen a tus próximas compras" : (guardadoOk ? "✅ Guardado" : "✅ Todo guardado")}
        </span>
        <button onClick={guardar} disabled={guardando || !dirty} style={{
          background: dirty ? "#fff" : "#e2e8f0", color: dirty ? "#dc2626" : "#94a3b8",
          border: "none", borderRadius: 10, padding: "10px 22px", fontWeight: 800, fontSize: 14,
          cursor: dirty && !guardando ? "pointer" : "not-allowed",
        }}>
          {guardando ? "Guardando…" : "💾 Guardar"}
        </button>
      </div>

      {fotoAmpliada && (
        <div
          onClick={() => setFotoAmpliada(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 3000, background: "rgba(15,23,42,0.85)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            padding: 24, cursor: "zoom-out",
          }}
        >
          <AuthImage src={imagenSrc(fotoAmpliada)} alt={fotoAmpliada.nombre} style={{
            maxWidth: "90vw", maxHeight: "75vh", objectFit: "contain", borderRadius: 12, background: "#fff",
          }} />
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 700, marginTop: 14, textAlign: "center" }}>
            {fotoAmpliada.nombre}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setFotoAmpliada(null); }}
            style={{
              position: "absolute", top: 16, right: 16, width: 38, height: 38, borderRadius: "50%",
              border: "none", background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 20,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
            title="Cerrar"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
